import { useState } from 'react';
import { Layout as LayoutIcon, Gauge, BarChart3, Table, Save, FolderPlus, RotateCcw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/shared/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { useToast } from '@/shared/hooks/use-toast';
import { useIsAdmin } from '@/shared/hooks/use-admin-validation';
import { useIsSuperAdminRole } from '@/shared/hooks/use-system-role';
import { useViewScope } from '@/shared/hooks/use-view-scope';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { useDashboardLayouts } from '../hooks/useDashboardLayouts';
import { useDashboardTabs } from '../hooks/useDashboardTabs';
import { isSuggestedLayoutId } from '../data/suggestedLayouts';
import { LayoutSlot } from '../types/Layout';
import { FormFactor } from '../utils/formFactor';
import { findFirstFitPosition } from '../utils/layoutHelpers';
import { DashboardService } from '../services/dashboardService';

/**
 * Layout toolbar button — now just an "Adicionar slot" menu.
 *
 * After the simplification: there is no "layout switching" anymore. Each tab
 * owns exactly one arrangement (created from the KPIs em destaque seed when
 * the tab is born). The user's edits — adding slots, dragging, resizing,
 * assigning dashboards — all persist directly to that arrangement.
 *
 * Edge case: legacy tabs that still point at a suggested template id are
 * read-only by design (suggested templates are hardcoded). The first time
 * the user adds a slot from such a tab, we transparently fork the suggested
 * slots into a user-owned custom layout in a single `createLayout` call,
 * so the seed slots come along with the new one.
 */
const DEFAULT_SLOT_GEOMETRY: Record<
  FormFactor,
  { size: LayoutSlot['size']; w: number; h: number }
> = {
  tile: { size: 'small', w: 2, h: 2 },
  graph: { size: 'medium', w: 6, h: 5 },
  table: { size: 'full', w: 12, h: 6 },
  hero: { size: 'full', w: 12, h: 6 },
};

export const LayoutSwitcher = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { activeLayout, createLayout, addSlot } = useDashboardLayouts();
  // PBI #78540: when there are no tabs yet, the DashboardTabBar renders
  // nothing — meaning the user has no entry point to ever create one. We
  // surface "Adicionar aba" inside this Layout menu in that case so the
  // UX has a discoverable way to opt into tabs.
  const { hasTabs, addTab } = useDashboardTabs();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { scope: viewScope } = useViewScope();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();

  // PBI #78540 — "Salvar como Padrão" state + handlers.
  const [confirmTarget, setConfirmTarget] = useState<
    null | { type: 'org' | 'product' | 'platform'; scope: 'superadmin' | 'organization' | 'product' }
  >(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const queryClient = useQueryClient();

  const resolverScope: 'superadmin' | 'organization' | 'product' =
    viewScope === 'platform' ? 'superadmin' : viewScope;
  // CRITICAL: must come from `selectedOrganization` (superadmin's currently-
  // viewed org) first, falling back to session for regular users. Same pattern
  // as useDashboards / useDashboardLayouts / useDashboardTabs — without it,
  // Save-as-Default keys the read on the wrong (orgId, productId) tuple and
  // ends up snapshotting empty / wrong-scope data.
  const { selectedOrganization } = useSuperAdminOrg();
  const effectiveOrgId = selectedOrganization?.id ?? user?.organizationId ?? undefined;
  const productId = viewScope === 'product' ? currentWorkspace?.id : undefined;
  const canSaveOrgOrProduct =
    isAdmin &&
    ((viewScope === 'organization' && !!effectiveOrgId) ||
      (viewScope === 'product' && !!effectiveOrgId && !!productId));
  const canSavePlatformDefault = isSuperAdmin;
  const showSaveDefaultSection = canSaveOrgOrProduct || canSavePlatformDefault;

  const handleConfirmSaveDefault = async () => {
    if (!confirmTarget) return;
    setSavingDefault(true);
    try {
      const asPlatformDefault = confirmTarget.type === 'platform';
      await DashboardService.setAsDefault({
        scope: confirmTarget.scope,
        orgId: confirmTarget.scope === 'superadmin' ? undefined : effectiveOrgId,
        productId: confirmTarget.scope === 'product' ? productId : undefined,
        asPlatformDefault,
      });
      toast({
        title: t(
          asPlatformDefault
            ? 'dashboards.defaults.savedPlatform'
            : confirmTarget.type === 'org'
              ? 'dashboards.defaults.savedOrg'
              : 'dashboards.defaults.savedWorkspace',
          'Layout salvo como padrão.',
        ),
      });
    } catch (err) {
      toast({
        title: t('dashboards.defaults.saveError', 'Falha ao salvar como padrão.'),
        description: resolveApiErrorMessage(err, t),
        variant: 'destructive',
      });
    } finally {
      setSavingDefault(false);
      setConfirmTarget(null);
    }
  };

  const handleResetPreferences = async () => {
    setResetting(true);
    try {
      await DashboardService.resetUserPreferences({
        scope: resolverScope,
        orgId: resolverScope === 'superadmin' ? undefined : effectiveOrgId,
        productId: resolverScope === 'product' ? productId : undefined,
      });
      toast({
        title: t('dashboards.defaults.resetSuccess', 'Preferências resetadas.'),
      });
      // Force everything to refetch — prefs, layouts, tabs, dashboards list.
      queryClient.invalidateQueries({ queryKey: ['dashboard-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['available-dashboards'] });
    } catch (err) {
      toast({
        title: t('dashboards.defaults.resetError', 'Falha ao resetar preferências.'),
        description: resolveApiErrorMessage(err, t),
        variant: 'destructive',
      });
    } finally {
      setResetting(false);
      setResetOpen(false);
    }
  };

  const confirmMessage = () => {
    if (!confirmTarget) return '';
    if (confirmTarget.type === 'platform') {
      if (viewScope === 'platform') {
        return t(
          'dashboards.defaults.confirmSuperadmin',
          'Salvar este layout como padrão da tela Superadmin? Todos os superadmins sem layout próprio passarão a vê-lo.',
        );
      }
      if (viewScope === 'organization') {
        return t(
          'dashboards.defaults.confirmAllOrgs',
          'Salvar este layout como padrão global para TODAS as organizações? Orgs sem layout próprio e usuários sem customização passarão a vê-lo.',
        );
      }
      return t(
        'dashboards.defaults.confirmAllWorkspaces',
        'Salvar este layout como padrão global para TODOS os workspaces? Workspaces sem layout próprio e usuários sem customização passarão a vê-lo.',
      );
    }
    if (confirmTarget.type === 'org') {
      return t(
        'dashboards.defaults.confirmOrg',
        'Salvar este layout como padrão da organização? Usuários sem layout próprio passarão a vê-lo.',
      );
    }
    return t(
      'dashboards.defaults.confirmWorkspace',
      'Salvar este layout como padrão do workspace? Usuários sem layout próprio neste workspace passarão a vê-lo.',
    );
  };

  const activeIsSuggested = activeLayout ? isSuggestedLayoutId(activeLayout.id) : false;

  const handleAddSlot = async (formFactor: FormFactor) => {
    const cfg = DEFAULT_SLOT_GEOMETRY[formFactor];
    const customName = t('dashboards.layouts.customName', 'Personalizado');

    // No active layout → create a fresh custom layout seeded with this slot.
    if (!activeLayout) {
      const slot: LayoutSlot = {
        id: uuidv4(),
        formFactor,
        size: cfg.size,
        x: 0,
        y: 0,
        w: cfg.w,
        h: cfg.h,
      };
      await createLayout(customName, [slot]);
      return;
    }

    // Suggested (read-only) → fork into custom with seed slots + new one in ONE
    // call. A two-step duplicate-then-addSlot races against the React render
    // cycle: by the time `addSlot` runs the hook hasn't re-rendered with the
    // duplicated layout yet, so the lookup misses and it silently no-ops.
    if (activeIsSuggested) {
      const { x, y } = findFirstFitPosition(activeLayout.slots, cfg.w, cfg.h);
      const newSlot: LayoutSlot = {
        id: uuidv4(),
        formFactor,
        size: cfg.size,
        x,
        y,
        w: cfg.w,
        h: cfg.h,
      };
      const seededSlots = [...activeLayout.slots.map((s) => ({ ...s })), newSlot];
      await createLayout(customName, seededSlots);
      return;
    }

    // Custom layout already active → just add the slot.
    const { x, y } = findFirstFitPosition(activeLayout.slots, cfg.w, cfg.h);
    await addSlot(activeLayout.id, { formFactor, size: cfg.size, x, y, w: cfg.w, h: cfg.h });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            title={t('dashboards.layouts.addSlot', 'Adicionar slot')}
            aria-label={t('dashboards.layouts.addSlot', 'Adicionar slot')}
            className="bg-background border hover:bg-accent"
          >
            <LayoutIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {t('dashboards.layouts.addSlot', 'Adicionar slot')}
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleAddSlot('tile')}>
            <Gauge className="h-4 w-4 mr-2" />
            {t('dashboards.formFactor.tile', 'Tile')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleAddSlot('graph')}>
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('dashboards.formFactor.graph', 'Gráfico')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleAddSlot('table')}>
            <Table className="h-4 w-4 mr-2" />
            {t('dashboards.formFactor.table', 'Tabela')}
          </DropdownMenuItem>

          {/* PBI #78540 — When no tabs exist yet, the tab bar is hidden;
              surface a "Adicionar aba" entry here so the user can opt in. */}
          {!hasTabs && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => addTab()}>
                <FolderPlus className="h-4 w-4 mr-2" />
                {t('dashboards.tabs.addTab', 'Adicionar aba')}
              </DropdownMenuItem>
            </>
          )}

          {/* PBI #78540 — "Salvar como Padrão" embedded in the layout menu */}
          {showSaveDefaultSection && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t('dashboards.defaults.menuTitle', 'Salvar como Padrão')}
              </DropdownMenuLabel>
              {canSaveOrgOrProduct && viewScope === 'organization' && (
                <DropdownMenuItem
                  onClick={() => setConfirmTarget({ type: 'org', scope: 'organization' })}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {t('dashboards.defaults.forOrganization', 'Organização')}
                </DropdownMenuItem>
              )}
              {canSaveOrgOrProduct && viewScope === 'product' && (
                <DropdownMenuItem
                  onClick={() => setConfirmTarget({ type: 'product', scope: 'product' })}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {t('dashboards.defaults.forWorkspace', 'Workspace')}
                </DropdownMenuItem>
              )}
              {canSavePlatformDefault && (
                <DropdownMenuItem
                  onClick={() => setConfirmTarget({ type: 'platform', scope: resolverScope })}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {viewScope === 'platform'
                    ? t('dashboards.defaults.forSuperadmin', 'Superadmin')
                    : viewScope === 'organization'
                      ? t('dashboards.defaults.forAllOrgs', 'Todas as Organizações')
                      : t('dashboards.defaults.forAllWorkspaces', 'Todos os Workspaces')}
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* PBI #78540 — Reset to inherited default. Always visible; reset
              of an empty slot is a no-op server-side. */}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setResetOpen(true)} className="text-destructive focus:text-destructive">
            <RotateCcw className="h-4 w-4 mr-2" />
            {t('dashboards.defaults.resetPreferences', 'Limpar Preferências')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={!!confirmTarget}
        onOpenChange={(open) => !open && setConfirmTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboards.defaults.confirmTitle', 'Confirmar')}</AlertDialogTitle>
            <AlertDialogDescription>{confirmMessage()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingDefault}>
              {t('common.cancel', 'Cancelar')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSaveDefault} disabled={savingDefault}>
              {savingDefault
                ? t('common.saving', 'Salvando...')
                : t('common.confirm', 'Confirmar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetOpen} onOpenChange={(open) => !open && setResetOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('dashboards.defaults.resetTitle', 'Limpar preferências?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {viewScope === 'platform'
                ? t('dashboards.defaults.resetSuperadminMsg', 'Suas preferências de superadmin serão apagadas. Você passará a ver o layout default global da plataforma. Esta ação não pode ser desfeita.')
                : viewScope === 'organization'
                  ? t('dashboards.defaults.resetOrgMsg', 'Suas preferências desta organização serão apagadas. Você passará a ver o layout default da organização (ou o global, se não houver). Esta ação não pode ser desfeita.')
                  : t('dashboards.defaults.resetWorkspaceMsg', 'Suas preferências deste workspace serão apagadas. Você passará a ver o layout default do workspace (ou o global). Esta ação não pode ser desfeita.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>
              {t('common.cancel', 'Cancelar')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetPreferences}
              disabled={resetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {resetting
                ? t('common.saving', 'Limpando...')
                : t('dashboards.defaults.resetConfirm', 'Limpar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
